import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Mail, Users, Send, Eye, Clock, MessageSquare, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * Admin + Coach — Bulk email campaigns.
 *
 * Compose a subject + body, dial in recipient filters (sport, sex, DUPR band,
 * session history, or attendees of a specific offering) and preview the
 * matched count before firing. Backed by /api/emails/preview and /api/emails/send.
 *
 * Coaches only see their own players (server-side gate). Admins see everyone.
 */
const SEX_OPTIONS = [
  { v: "", label: "All" },
  { v: "M", label: "Male" },
  { v: "F", label: "Female" },
  { v: "Other", label: "Other" },
];
const SESSION_TYPE_OPTIONS = [
  { v: "one_on_one", label: "1:1" },
  { v: "performance", label: "Performance" },
  { v: "group", label: "Group" },
];

export default function AdminEmails() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [subject, setSubject] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [filters, setFilters] = useState({
    sport: "",
    sex: "",
    level_min: "",
    level_max: "",
    session_types: [],
    offering_id: searchParams.get("offering_id") || "",
  });

  // React to ?offering_id=… changes when the user comes in from another
  // 'Message attendees' link within the same session.
  useEffect(() => {
    const qp = searchParams.get("offering_id") || "";
    setFilters((f) => (f.offering_id === qp ? f : { ...f, offering_id: qp }));
  }, [searchParams]);
  const [preview, setPreview] = useState(null);       // { count, sample }
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [offerings, setOfferings] = useState([]);
  const [history, setHistory] = useState([]);

  const isAdmin = user?.role === "admin" || (user?.permissions || []).includes("admin") || (user?.permissions || []).includes("super_admin");

  useEffect(() => {
    // Own offerings for coaches; all-time offerings for admins (server-side gated).
    api.get("/offerings?only_upcoming=false").then((r) => setOfferings(r.data || [])).catch(() => setOfferings([]));
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/emails/campaigns?limit=20");
      setHistory(data || []);
    } catch (_e) { /* silent */ }
  };

  const anySending = history.some((c) => c.status === "sending");
  // Poll while at least one campaign is still 'sending' so the counter ticks.
  useEffect(() => {
    if (!anySending) return undefined;
    const t = setInterval(loadHistory, 4000);
    return () => clearInterval(t);
  }, [anySending]);

  const payload = useMemo(() => {
    const f = {};
    if (filters.sport) f.sport = filters.sport;
    if (filters.sex) f.sex = filters.sex;
    if (filters.level_min !== "") f.level_min = parseFloat(filters.level_min);
    if (filters.level_max !== "") f.level_max = parseFloat(filters.level_max);
    if (filters.session_types.length) f.session_types = filters.session_types;
    if (filters.offering_id) f.offering_id = filters.offering_id;
    return f;
  }, [filters]);

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post("/emails/preview", { filters: payload });
      setPreview(data);
      if (data.count === 0) toast.message("No players match those filters yet");
      else toast.success(`${data.count} player${data.count === 1 ? "" : "s"} would receive this`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const send = async () => {
    if (subject.trim().length < 2 || bodyText.trim().length < 2) {
      toast.error("Add a subject + body (min 2 chars each)");
      return;
    }
    if (!preview) {
      toast.message("Run a preview first so you can see who this goes to");
      return;
    }
    if (!window.confirm(`Send to ${preview.count} player${preview.count === 1 ? "" : "s"}? This can't be undone.`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/emails/send", {
        subject: subject.trim(),
        body_text: bodyText.trim(),
        filters: payload,
      });
      // Backend now fans out in the background; we get an immediate 200
      // with { id, target_count, status: 'sending' }. Poll history so the
      // new campaign appears with a live sent_count.
      toast.success(`Sending to ${data.target_count} in the background — you can close this page.`);
      setSubject(""); setBodyText(""); setPreview(null);
      await loadHistory();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const toggleSessionType = (v) => {
    setFilters((f) => ({
      ...f,
      session_types: f.session_types.includes(v)
        ? f.session_types.filter((x) => x !== v)
        : [...f.session_types, v],
    }));
    setPreview(null);
  };

  const updateFilter = (patch) => { setFilters((f) => ({ ...f, ...patch })); setPreview(null); };

  return (
    <div className="space-y-6" data-testid="admin-emails-page">
      <div>
        {isAdmin && (
          <Link to="/admin" className="text-xs uppercase tracking-wider text-[#A3A3A3] hover:text-[#CCFF00] inline-flex items-center gap-2 mb-3" data-testid="back-to-admin">
            <ArrowLeft className="w-3 h-3" /> Admin overview
          </Link>
        )}
        <div className="label-mono mb-2" style={{ color: "#FF4500" }}>BULK EMAIL CAMPAIGNS</div>
        <h1 className="h-display text-5xl tracking-tighter">Blanket message the right players.</h1>
        <p className="text-sm text-[#A3A3A3] mt-2 max-w-2xl">
          Filter by sport, sex, DUPR level, past session type or the attendees of a specific offering.
          Preview the exact count first — then send via email + in-app notification.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Compose */}
        <div className="lg:col-span-2 space-y-4">
          <div className="card-surface p-5" data-testid="compose-panel">
            <div className="label-mono mb-3 flex items-center gap-2"><MessageSquare className="w-3.5 h-3.5" /> COMPOSE</div>
            <label className="label-mono text-[10px] text-[#A3A3A3] mb-1 block">SUBJECT</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Kitchen drills every Tuesday 6:30 pm — from £8 pp"
              maxLength={140}
              className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#CCFF00] px-3 py-2 text-sm outline-none mb-3"
              data-testid="email-subject-input"
            />
            <label className="label-mono text-[10px] text-[#A3A3A3] mb-1 block">MESSAGE</label>
            <textarea
              value={bodyText}
              onChange={(e) => setBodyText(e.target.value)}
              rows={10}
              maxLength={8000}
              placeholder="Hi — heads up on our new midweek kitchen drills sessions..."
              className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#CCFF00] px-3 py-2 text-sm outline-none leading-relaxed"
              data-testid="email-body-input"
            />
            <div className="text-[10px] text-[#525252] mt-1 uppercase tracking-wider">
              {bodyText.length} / 8000 chars — your name + reply-to email is auto-appended.
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={runPreview}
              disabled={previewing}
              className="px-5 py-3 text-xs uppercase tracking-wider border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black inline-flex items-center gap-2 disabled:opacity-40"
              data-testid="preview-recipients-btn"
            >
              <Eye className="w-3.5 h-3.5" /> {previewing ? "Counting…" : "Preview recipients"}
            </button>
            <button
              type="button"
              onClick={send}
              disabled={sending || !preview || preview.count === 0}
              className="btn-volt px-5 py-3 text-xs inline-flex items-center gap-2 disabled:opacity-30"
              data-testid="send-campaign-btn"
            >
              <Send className="w-3.5 h-3.5" /> {sending ? "Sending…" : preview ? `Send to ${preview.count}` : "Send"}
            </button>
          </div>

          {preview && (
            <div className="card-surface p-5" data-testid="preview-result">
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-4 h-4 text-[#CCFF00]" />
                <div className="label-mono">MATCHED · <span className="text-[#CCFF00]" data-testid="preview-count">{preview.count}</span> player{preview.count === 1 ? "" : "s"}</div>
              </div>
              {preview.sample?.length ? (
                <div className="space-y-1">
                  {preview.sample.map((r, i) => (
                    <div key={i} className="text-sm text-[#A3A3A3] flex items-center gap-2" data-testid={`preview-row-${i}`}>
                      <span className="text-white">{r.name || "—"}</span>
                      <span className="text-[#525252]">·</span>
                      <span className="text-[#A3A3A3]">{r.email_masked}</span>
                    </div>
                  ))}
                  {preview.count > preview.sample.length && (
                    <div className="text-xs text-[#525252] mt-2">+ {preview.count - preview.sample.length} more</div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-[#525252]">No players match — loosen the filters.</div>
              )}
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="card-surface p-5 space-y-4 h-max" data-testid="filters-panel">
          <div className="label-mono flex items-center gap-2"><Users className="w-3.5 h-3.5" /> RECIPIENT FILTERS</div>

          <div>
            <label className="label-mono text-[10px] text-[#A3A3A3] mb-1 block">SPORT</label>
            <select
              value={filters.sport}
              onChange={(e) => updateFilter({ sport: e.target.value })}
              className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#CCFF00] px-3 py-2 text-sm outline-none"
              data-testid="filter-sport"
            >
              <option value="">All sports</option>
              <option value="pickleball">Pickleball</option>
              <option value="squash">Squash</option>
            </select>
          </div>

          <div>
            <label className="label-mono text-[10px] text-[#A3A3A3] mb-1 block">SEX</label>
            <select
              value={filters.sex}
              onChange={(e) => updateFilter({ sex: e.target.value })}
              className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#CCFF00] px-3 py-2 text-sm outline-none"
              data-testid="filter-sex"
            >
              {SEX_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
          </div>

          <div>
            <label className="label-mono text-[10px] text-[#A3A3A3] mb-1 block">DUPR LEVEL RANGE</label>
            <div className="flex items-center gap-2">
              <input
                type="number" step="0.5" min="1" max="6"
                value={filters.level_min}
                onChange={(e) => updateFilter({ level_min: e.target.value })}
                placeholder="min"
                className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#CCFF00] px-3 py-2 text-sm outline-none"
                data-testid="filter-level-min"
              />
              <span className="text-[#525252]">–</span>
              <input
                type="number" step="0.5" min="1" max="6"
                value={filters.level_max}
                onChange={(e) => updateFilter({ level_max: e.target.value })}
                placeholder="max"
                className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#CCFF00] px-3 py-2 text-sm outline-none"
                data-testid="filter-level-max"
              />
            </div>
          </div>

          <div>
            <label className="label-mono text-[10px] text-[#A3A3A3] mb-1 block">SESSION HISTORY (any of)</label>
            <div className="flex flex-wrap gap-2">
              {SESSION_TYPE_OPTIONS.map((o) => {
                const on = filters.session_types.includes(o.v);
                return (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() => toggleSessionType(o.v)}
                    className="px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors"
                    style={on ? { borderColor: "#CCFF00", background: "#CCFF00", color: "#0A0A0A", fontWeight: 700 } : { borderColor: "#262626", color: "#A3A3A3" }}
                    data-testid={`filter-session-${o.v}`}
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="label-mono text-[10px] text-[#A3A3A3] mb-1 block">SCOPE TO AN OFFERING (attendees only)</label>
            <select
              value={filters.offering_id}
              onChange={(e) => updateFilter({ offering_id: e.target.value })}
              className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#CCFF00] px-3 py-2 text-sm outline-none"
              data-testid="filter-offering"
            >
              <option value="">Any offering</option>
              {offerings.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.title || `${o.session_type} · ${o.start?.slice(0, 10)}`}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Campaign history */}
      <div className="card-surface p-5" data-testid="campaign-history">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-4 h-4 text-[#A3A3A3]" />
          <div className="label-mono">RECENT CAMPAIGNS</div>
        </div>
        {history.length === 0 ? (
          <div className="text-sm text-[#525252]">No campaigns sent yet.</div>
        ) : (
          <div className="space-y-2">
            {history.map((c) => (
              <div key={c.id} className="border border-[#262626] p-3 flex flex-wrap items-center justify-between gap-3" data-testid={`campaign-${c.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{c.subject}</div>
                  <div className="text-[10px] uppercase tracking-wider text-[#525252] mt-0.5">
                    by {c.sender_name || c.sender_email || "—"} · {c.created_at ? new Date(c.created_at).toLocaleString() : "—"}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="label-mono text-xs" style={{ color: c.status === "sent" ? "#CCFF00" : c.status === "sending" ? "#FF4500" : c.status === "failed" ? "#EF4444" : "#A3A3A3" }}>
                    {(c.sent_count ?? 0)}/{c.target_count || 0} · {c.status?.toUpperCase()}
                    {c.status === "sending" && <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-[#FF4500] animate-pulse" />}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card-surface p-5 border-l-4 border-[#CCFF00]" data-testid="tips-panel">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-[#CCFF00] mt-0.5 shrink-0" />
          <div>
            <div className="label-mono mb-1" style={{ color: "#CCFF00" }}>TIPS</div>
            <ul className="text-xs text-[#A3A3A3] leading-relaxed space-y-1.5 list-disc list-inside">
              <li>Max 500 recipients per campaign — narrow the filters when the count runs high.</li>
              <li>{isAdmin ? "Admins can reach every player. " : "Coaches only see players they&apos;ve actually coached. "}Attendees of a specific offering are always in scope.</li>
              <li>Every recipient also gets an in-app notification so the message doesn&apos;t get lost in an inbox.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
